import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Eye, EyeSlash, CheckCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) navigate("/forgot-password", { replace: true });
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      if (setUser) setUser(data);
      setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong — please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col justify-center px-6 py-12" data-testid="reset-password-page">
      <div className="max-w-md w-full mx-auto vv-fade-up">
        {!done ? (
          <>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-medium text-[#0D1B2A] leading-tight mb-3">Set a new password</h1>
            <p className="text-[15px] text-[#525252] leading-relaxed mb-8">
              Pick something you'll remember this time. At least 6 characters.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="label-caps block mb-1.5">New password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    data-testid="reset-password-new"
                    className="h-14 border border-[#0D1B2A]/30 bg-transparent rounded-none px-4 pr-12 text-[17px] focus:border-[#0D1B2A] focus:ring-1 focus:ring-[#0D1B2A] outline-none transition-all w-full"
                  />
                  <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A867D] hover:text-[#0D1B2A]" aria-label="Toggle password">
                    {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="label-caps block mb-1.5">Confirm password</label>
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Type it again"
                  data-testid="reset-password-confirm"
                  className="h-14 border border-[#0D1B2A]/30 bg-transparent rounded-none px-4 text-[17px] focus:border-[#0D1B2A] focus:ring-1 focus:ring-[#0D1B2A] outline-none transition-all w-full"
                />
              </div>
              <button
                type="submit"
                disabled={busy || !password || !confirm}
                data-testid="reset-password-submit"
                className="w-full h-14 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-50"
              >
                {busy ? "Saving…" : "Save & sign in"} <ArrowRight size={18} weight="bold" />
              </button>
            </form>

            <p className="text-sm text-[#8A867D] mt-6 text-center">
              <Link to="/login" className="hover:text-[#0D1B2A]">Back to sign in</Link>
            </p>
          </>
        ) : (
          <div data-testid="reset-password-done" className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#DCFCE7] border-2 border-[#86EFAC] mb-5">
              <CheckCircle size={30} weight="fill" className="text-[#166534]" />
            </div>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-medium text-[#0D1B2A] leading-tight mb-3">Password updated</h1>
            <p className="text-[15px] text-[#525252]">Signing you in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
